import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  ScrollArea,
} from "@devhop/ui";
import type { UpdateRoomBookingDTO } from "../../domain/contracts/room-bookings";
import { useRoomBookingQuery, useUpdateRoomBooking } from "../api/queries";
import { RoomBookingForm } from "./RoomBookingForm";

type EditRoomBookingDialogProps = {
  roomBookingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function EditRoomBookingDialog({
  roomBookingId,
  open,
  onOpenChange,
  onSuccess,
}: EditRoomBookingDialogProps) {
  const { data: booking, isLoading } = useRoomBookingQuery(roomBookingId ?? "");
  const updateBooking = useUpdateRoomBooking(roomBookingId ?? "");

  const canEdit = booking && booking.status === "scheduled";

  const initialValues =
    booking &&
    ({
      roomId: booking.roomId,
      date: booking.date,
      startTime:
        booking.startTime.length === 8
          ? booking.startTime.slice(0, 5)
          : booking.startTime,
      endTime:
        booking.endTime.length === 8
          ? booking.endTime.slice(0, 5)
          : booking.endTime,
      contactName: booking.contactName ?? "",
      contactPhone: booking.contactPhone ?? "",
      title: booking.title ?? "",
      chargeType: (booking.amount != null ? "paid" : "free") as "free" | "paid",
      amount: booking.amount,
      notes: booking.notes ?? "",
    } as const);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="!max-w-[min(95vw,1200px)] max-h-[90vh] overflow-hidden p-0"
          showCloseButton
        >
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>ແກ້ໄຂການຈອງຫ້ອງ</DialogTitle>
          </DialogHeader>
          {isLoading || !booking ? (
            <div className="px-6 py-8 text-center text-muted-foreground">
              ກຳລັງໂຫຼດ...
            </div>
          ) : !canEdit ? (
            <div className="space-y-4 px-6 py-4">
              <p className="text-muted-foreground">
                ບໍ່ສາມາດແກ້ໄຂການຈອງທີ່ສຳເລັດຫຼືຍົກເລີກແລ້ວ
              </p>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  ປິດ
                </Button>
              </div>
            </div>
          ) : initialValues ? (
            <ScrollArea className="max-h-[calc(90vh-4rem)] overflow-auto px-6 pb-6">
              <RoomBookingForm
                mode="edit"
                initialValues={initialValues}
                excludeRoomBookingId={roomBookingId ?? undefined}
                onSubmit={async (vals) => {
                  try {
                    await updateBooking.mutateAsync(
                      vals as UpdateRoomBookingDTO,
                    );
                    onSuccess?.();
                    onOpenChange(false);
                  } catch {
                    // Error handled by mutation
                  }
                }}
                submitting={updateBooking.isPending}
              />
            </ScrollArea>
          ) : null}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
